import React, { useMemo, useRef, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbIcon,
  WxbKpiCard,
  WxbModal,
  WxbSelect,
  WxbSpinner,
  WxbTag,
  WxbUpload,
  WxbValidationList,
} from '../wxb-ui';
import {
  downloadResourceTopologyImportTemplate,
  isResourceTopologyImportPreviewCommittable,
  parseResourceTopologyImportFile,
  type ParsedResourceTopologyImport,
  type ResourceTopologyImportParseIssue,
} from './resourceTopologyImport';
import {
  normalizeResourceTopologyError,
  prodResourceTopologyApi,
} from '../../services/prodResourceTopologyApi';
import type {
  ResourceTopologyImportCommitResult,
  ResourceTopologyImportMode,
  ResourceTopologyImportPreview,
  ResourceTopologyImportPreviewRow,
  ResourceTopologyImportScope,
  ResourceTopologyIssue,
} from '../../types/prodResourceTopology';
import './ResourceTopologyImportModal.css';

interface ResourceTopologyImportModalProps {
  open: boolean;
  versionId: string;
  revision: number;
  canWrite: boolean;
  onClose: () => void;
  onRefreshRevision: () => Promise<void>;
  onCommitted: (result: ResourceTopologyImportCommitResult) => Promise<void> | void;
}

const MAX_RENDERED_ROW_ISSUES = 500;
const MAX_RENDERED_VALIDATION_ISSUES = 200;

const statusLabel = (status: ResourceTopologyImportPreviewRow['status']) => (
  status === 'CREATE' ? '新增' : status === 'UPDATE' ? '覆盖' : '错误'
);

const ResourceTopologyImportModal: React.FC<ResourceTopologyImportModalProps> = ({
  open,
  versionId,
  revision,
  canWrite,
  onClose,
  onRefreshRevision,
  onCommitted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedResourceTopologyImport | null>(null);
  const [preview, setPreview] = useState<ResourceTopologyImportPreview | null>(null);
  const [requestIssues, setRequestIssues] = useState<ResourceTopologyIssue[]>([]);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestErrorTitle, setRequestErrorTitle] = useState('导入请求未完成');
  const [mode, setMode] = useState<ResourceTopologyImportMode>('APPEND');
  const [scope, setScope] = useState<ResourceTopologyImportScope>('FULL');
  const [parsing, setParsing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const previewLockRef = useRef(false);
  const commitLockRef = useRef(false);

  const reset = () => {
    setFile(null);
    setParsed(null);
    setPreview(null);
    setRequestIssues([]);
    setRequestError(null);
    setRequestErrorTitle('导入请求未完成');
    setMode('APPEND');
    setScope('FULL');
  };

  const handleFilesSelected = async (files: File[]) => {
    if (parsing || previewing || committing || files.length === 0) return;
    const selected = files[0];
    setFile(selected);
    setParsed(null);
    setPreview(null);
    setRequestIssues([]);
    setRequestError(null);
    setRequestErrorTitle('导入请求未完成');
    setParsing(true);
    try {
      const next = await parseResourceTopologyImportFile(selected);
      setParsed(next);
      setMode(next.payload.mode ?? 'APPEND');
      setScope(next.payload.scope ?? 'FULL');
    } catch (error) {
      setParsed({
        payload: { mode: 'APPEND', scope: 'FULL', resources: [], relationships: [] },
        issues: [{
          id: '工作簿-file-IMPORT_FILE_PARSE_FAILED-0',
          sheet: '工作簿',
          rowNumber: null,
          constraintCode: 'IMPORT_FILE_PARSE_FAILED',
          message: error instanceof Error ? error.message : '无法解析工作簿',
        }],
      });
    } finally {
      setParsing(false);
    }
  };

  const handleRequestFailure = async (error: unknown) => {
    setRequestErrorTitle('导入请求未完成');
    const normalized = normalizeResourceTopologyError(error);
    setRequestIssues(normalized.kind === 'request-failure' ? normalized.issues : []);
    if (normalized.kind === 'revision-conflict') {
      setRequestError(`${normalized.message}。文件和解析结果已保留，请重新预检。`);
      try {
        await onRefreshRevision();
      } catch {
        // The original conflict remains actionable even if refreshing the shell fails.
      }
      return;
    }
    if (normalized.status === 409) {
      setRequestError(`${normalized.message}。文件和解析结果已保留，请重新预检。`);
      try {
        await onRefreshRevision();
      } catch {
        // Keep the import payload available for a later manual retry.
      }
      return;
    }
    setRequestError(normalized.message);
  };

  const runPreview = async () => {
    if (!parsed || parsed.issues.length > 0 || previewing || committing || !canWrite || previewLockRef.current) return;
    previewLockRef.current = true;
    setPreviewing(true);
    setRequestError(null);
    setRequestIssues([]);
    try {
      setPreview(await prodResourceTopologyApi.previewImport(versionId, revision, {
        ...parsed.payload,
        mode,
        scope,
      }));
    } catch (error) {
      await handleRequestFailure(error);
    } finally {
      previewLockRef.current = false;
      setPreviewing(false);
    }
  };

  const downloadTemplate = async () => {
    setRequestError(null);
    try {
      await downloadResourceTopologyImportTemplate();
    } catch {
      setRequestErrorTitle('模板下载失败');
      setRequestError('XLSX 模板生成失败，请稍后重试。');
    }
  };

  const hardIssueCount = preview?.issues.filter((issue) => issue.hardOrSoft === 'hard').length ?? 0;
  const previewHasErrors = Boolean(
    preview
    && (preview.summary.errorCount > 0
      || preview.rows.some((row) => row.status === 'ERROR')
      || hardIssueCount > 0),
  );
  const previewIsStale = Boolean(preview && preview.baseRevision !== revision);
  const canCommit = Boolean(
    parsed
    && isResourceTopologyImportPreviewCommittable(preview, revision)
    && canWrite
    && !previewing
    && !committing,
  );

  const commit = async () => {
    if (!canCommit || !parsed || !preview || commitLockRef.current) return;
    commitLockRef.current = true;
    setCommitting(true);
    setRequestError(null);
    setRequestIssues([]);
    let result: ResourceTopologyImportCommitResult;
    try {
      result = await prodResourceTopologyApi.commitImport(
        versionId,
        revision,
        preview.previewToken,
        { ...parsed.payload, mode, scope },
      );
    } catch (error) {
      await handleRequestFailure(error);
      commitLockRef.current = false;
      setCommitting(false);
      return;
    }

    reset();
    try {
      await onCommitted(result);
    } catch {
      setRequestErrorTitle('导入已成功，页面刷新失败');
      setRequestError('追加导入已成功，但页面刷新失败。请关闭弹窗后手动刷新，勿重复提交同一文件。');
    } finally {
      commitLockRef.current = false;
      setCommitting(false);
    }
  };

  const parseIssueColumns: ColumnsType<ResourceTopologyImportParseIssue> = [
    { title: 'Sheet', dataIndex: 'sheet', width: 110 },
    { title: '行', dataIndex: 'rowNumber', width: 64, render: (value: number | null) => value ?? '—' },
    {
      title: 'constraint_code',
      dataIndex: 'constraintCode',
      width: 230,
      render: (value: string) => <span className="resource-import-code">{value}</span>,
    },
    { title: '问题', dataIndex: 'message' },
  ];
  const previewColumns: ColumnsType<ResourceTopologyImportPreviewRow> = [
    { title: 'Sheet', dataIndex: 'sheet', width: 110 },
    { title: '行', dataIndex: 'rowNumber', width: 64 },
    {
      title: '结果',
      dataIndex: 'status',
      width: 84,
      render: (value: ResourceTopologyImportPreviewRow['status']) => (
        <WxbTag color={value === 'CREATE' ? 'green' : value === 'UPDATE' ? 'blue' : 'red'}>{statusLabel(value)}</WxbTag>
      ),
    },
    { title: '对象', dataIndex: 'code', width: 150, render: (value: string | null) => value || '—' },
    {
      title: 'constraint_code',
      dataIndex: 'constraintCode',
      width: 230,
      render: (value: string | null) => value
        ? <span className="resource-import-code">{value}</span>
        : '—',
    },
    { title: '说明', dataIndex: 'message', render: (value: string | null) => value || '—' },
  ];

  const fileList = file ? [{
    uid: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    status: parsed?.issues.length ? 'error' as const : 'done' as const,
  }] : [];
  const parsedCounts = useMemo(() => ({
    resources: parsed?.payload.resources.length ?? 0,
    relationships: parsed?.payload.relationships.length ?? 0,
  }), [parsed]);

  const footer = (
    <div className="resource-import-footer">
      <WxbButton type="button" variant="ghost" disabled={committing} onClick={onClose}>
        {file ? '关闭（保留文件）' : '关闭'}
      </WxbButton>
      <div className="resource-import-footer-actions">
        <WxbButton
          type="button"
          variant="secondary"
          disabled={!parsed || parsed.issues.length > 0 || parsing || previewing || committing || !canWrite}
          onClick={runPreview}
        >
          {previewing ? '预检中…' : preview ? '重新预检' : '生成预览'}
        </WxbButton>
        <WxbButton type="button" disabled={!canCommit} onClick={commit}>
          {committing ? '导入中…' : mode === 'REPLACE' ? '确认覆盖导入' : '确认新增导入'}
        </WxbButton>
      </div>
    </div>
  );

  return (
    <WxbModal
      title="受控导入资源节点与关系"
      open={open}
      width={980}
      className="resource-import-modal"
      footer={footer}
      maskClosable={!committing}
      closable={!committing}
      onCancel={() => { if (!committing) onClose(); }}
    >
      <div className="resource-import-modal-body">
        <WxbAlert title="受控导入：先预检，再一次性提交">
          APPEND 只新增；REPLACE 仅覆盖工作簿中提供的既有行，不删除未提供的资源或关系。任何硬约束问题都会阻止整批提交。
        </WxbAlert>

        <div className="resource-import-toolbar">
          <div>
            <h3>1. 准备受控工作簿</h3>
            <p>模板内置枚举下拉、必填长度和数值范围限制；仅接受不超过 5 MB 的 .xlsx 文件。</p>
          </div>
          <WxbButton type="button" variant="secondary" onClick={downloadTemplate}>
            下载 XLSX 模板
          </WxbButton>
        </div>

        <WxbUpload
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          multiple={false}
          fileList={fileList}
          onFilesSelected={handleFilesSelected}
          className="resource-import-upload"
        >
          <div className="resource-import-upload-content">
            <WxbIcon name="batch-record" size={34} aria-hidden="true" />
            <strong>{file ? '选择其他工作簿' : '选择或拖入 XLSX 工作簿'}</strong>
            <span>公式单元格、错误 Sheet 或变更后的表头会在本地阻止预检</span>
          </div>
        </WxbUpload>

        {parsing && <WxbSpinner tip="正在解析工作簿…" />}

        {parsed && !parsing && (
          <section className="resource-import-section" aria-labelledby="resource-import-local-title">
            <div className="resource-import-section-heading">
              <div>
                <h3 id="resource-import-local-title">2. 本地解析</h3>
                <p>行号始终对应原始 Excel 物理行，便于直接回到文件修正。</p>
              </div>
              <WxbButton type="button" variant="ghost" size="sm" onClick={reset} disabled={previewing || committing}>
                清除文件
              </WxbButton>
            </div>
            <div className="resource-import-summary resource-import-summary-compact">
              <WxbKpiCard title="资源节点行" value={parsedCounts.resources} />
              <WxbKpiCard title="资源关系行" value={parsedCounts.relationships} />
              <WxbKpiCard title="本地错误" value={parsed.issues.length} />
            </div>
            <div className="resource-import-controls">
              <WxbSelect
                label="导入方式"
                value={mode}
                disabled={previewing || committing}
                options={[
                  { value: 'APPEND', label: '增量新增（只新增）' },
                  { value: 'REPLACE', label: '增量覆盖（覆盖已提供行）' },
                ]}
                onChange={(value) => {
                  setMode(value as ResourceTopologyImportMode);
                  setPreview(null);
                }}
              />
              <WxbSelect
                label="导入范围"
                value={scope}
                disabled={previewing || committing}
                options={[
                  { value: 'FULL', label: '资源与关系' },
                  { value: 'RESOURCES', label: '仅资源节点' },
                  { value: 'RELATIONSHIPS', label: '仅资源关系' },
                ]}
                onChange={(value) => {
                  setScope(value as ResourceTopologyImportScope);
                  setPreview(null);
                }}
              />
            </div>
            {parsed.issues.length > 0 ? (
              <>
                {parsed.issues.length > MAX_RENDERED_ROW_ISSUES && (
                  <WxbAlert title="错误较多">
                    共 {parsed.issues.length} 个本地错误，界面仅显示前 {MAX_RENDERED_ROW_ISSUES} 个；请先修正已显示问题后重新选择文件。
                  </WxbAlert>
                )}
                <WxbDataTable<ResourceTopologyImportParseIssue>
                  rowKey="id"
                  density="compact"
                  columns={parseIssueColumns}
                  dataSource={parsed.issues.slice(0, MAX_RENDERED_ROW_ISSUES)}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  scroll={{ x: 760 }}
                />
              </>
            ) : (
              <WxbTag color="green">本地格式检查通过，可以生成服务端预览</WxbTag>
            )}
          </section>
        )}

        {requestError && (
          <WxbAlert variant="error" title={requestErrorTitle}>
            {requestError}
          </WxbAlert>
        )}
        {requestIssues.length > 0 && (
          <>
            {requestIssues.length > MAX_RENDERED_VALIDATION_ISSUES && (
              <WxbAlert title="约束问题较多">
                共 {requestIssues.length} 个问题，界面仅显示前 {MAX_RENDERED_VALIDATION_ISSUES} 个。
              </WxbAlert>
            )}
            <WxbValidationList
              density="compact"
              items={requestIssues.slice(0, MAX_RENDERED_VALIDATION_ISSUES).map((item) => ({
                key: item.id,
                level: item.hardOrSoft === 'hard' ? 'error' : 'warning',
                title: item.constraintCode,
                description: item.violationReason,
                source: item.sheet && item.rowNumber
                  ? `${item.sheet} · 第 ${item.rowNumber} 行`
                  : item.hardOrSoft === 'hard' ? '硬约束' : '软约束',
              }))}
            />
          </>
        )}

        {preview && (
          <section className="resource-import-section" aria-labelledby="resource-import-preview-title">
            <div className="resource-import-section-heading">
              <div>
                <h3 id="resource-import-preview-title">3. 服务端预览</h3>
                <p>预览基于 revision {preview.baseRevision}，确认后按 {preview.mode === 'REPLACE' ? '覆盖' : '新增'}模式整批原子提交。</p>
              </div>
              <WxbTag color={previewHasErrors ? 'red' : 'green'}>
                {previewHasErrors ? '存在阻断问题' : '可以提交'}
              </WxbTag>
            </div>
            {previewIsStale && (
              <WxbAlert variant="error" title="预览已过期">
                当前草稿已变为 revision {revision}，文件已保留，请重新预检后再提交。
              </WxbAlert>
            )}
            <div className="resource-import-summary">
              <WxbKpiCard title="新增资源节点" value={preview.summary.createNodes} />
              <WxbKpiCard title="覆盖资源节点" value={preview.summary.updateNodes} />
              <WxbKpiCard title="新增资源关系" value={preview.summary.createRelationships} />
              <WxbKpiCard title="覆盖资源关系" value={preview.summary.updateRelationships} />
              <WxbKpiCard title="错误" value={preview.summary.errorCount} />
              <WxbKpiCard title="警告" value={preview.summary.warningCount} />
            </div>
            <WxbDataTable<ResourceTopologyImportPreviewRow>
              rowKey="id"
              density="compact"
              columns={previewColumns}
              dataSource={preview.rows}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: 900 }}
              emptyState={{ description: '预览没有返回逐行结果' }}
            />
            {preview.issues.length > 0 && (
              <>
                {preview.issues.length > MAX_RENDERED_VALIDATION_ISSUES && (
                  <WxbAlert title="预览问题较多">
                    共 {preview.issues.length} 个问题，界面仅显示前 {MAX_RENDERED_VALIDATION_ISSUES} 个。
                  </WxbAlert>
                )}
                <WxbValidationList
                  density="compact"
                  items={preview.issues.slice(0, MAX_RENDERED_VALIDATION_ISSUES).map((item) => ({
                    key: item.id,
                    level: item.hardOrSoft === 'hard' ? 'error' : item.severity === 'warning' ? 'warning' : 'info',
                    title: item.constraintCode,
                    description: item.suggestedAction
                      ? `${item.violationReason}；建议：${item.suggestedAction}`
                      : item.violationReason,
                    source: item.sheet && item.rowNumber
                      ? `${item.sheet} · 第 ${item.rowNumber} 行`
                      : item.hardOrSoft === 'hard' ? '硬约束' : '软约束',
                  }))}
                />
              </>
            )}
          </section>
        )}
      </div>
    </WxbModal>
  );
};

export default ResourceTopologyImportModal;
